import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash2, Check, X, Wallet, TrendingUp, TrendingDown, AlertCircle, CheckCircle2,
  Upload, Eye, FileImage, Banknote, Tag, Users as UsersIcon, User as UserIcon, ClipboardCheck, History,
  CreditCard, Copy, Settings, Save, Search, PiggyBank, Download, FileSpreadsheet, AlertOctagon, Send, BellRing, Mail,
} from "lucide-react";
import { isTreasurer, isAdminLike, CATEGORIES as CAT_KEYS, CATEGORY_LABEL } from "@/lib/roles";
import { downloadCsv } from "@/lib/csv";
import { fmtDate as fmtDateSafe } from "@/lib/dates";

// Use local date components so the default "today" reflects the user's
// wall-clock day (Chile UTC-4) rather than UTC.
function todayLocalIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

const STATUS_LABEL = {
  pending: { label: "Pendiente", cls: "text-[#FF9F0A] border-[#FF9F0A]" },
  pending_validation: { label: "Por validar", cls: "text-[#A78BFA] border-[#A78BFA]" },
  paid: { label: "Pagado", cls: "text-[#34C759] border-[#34C759]" },
  rejected: { label: "Rechazado", cls: "text-[#FF3B30] border-[#FF3B30]" },
};

const EXPENSE_CATEGORIES = [
  "arbitraje", "balones", "canchas", "transporte", "equipamiento", "competencia", "logistica", "otro",
];

const INCOME_CATEGORIES = [
  { key: "fondo_concursable", label: "Fondo concursable" },
  { key: "donacion",          label: "Donación" },
  { key: "sponsor",           label: "Sponsor / Auspicio" },
  { key: "evento",            label: "Evento / Recaudación" },
  { key: "beneficio",         label: "Beneficio" },
  { key: "premio",            label: "Premio torneo" },
  { key: "otro",              label: "Otro" },
];
const INCOME_LABEL = Object.fromEntries(INCOME_CATEGORIES.map(c => [c.key, c.label]));

const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString("es-CL")}`;
const fmtDate = (iso) => fmtDateSafe(iso, { day: "2-digit", month: "short", year: "numeric" });

export default function Finance() {
  const { user } = useAuth();
  const isAdmin = isAdminLike(user?.role);
  const canManage = isTreasurer(user?.role);

  if (isAdmin) {
    return <AdminFinanceView canManage={canManage} />;
  }
  return <PlayerFinanceView />;
}

// =================== ADMIN VIEW ===================
function AdminFinanceView({ canManage }) {
  const [tab, setTab] = useState("charges");
  const [dashboard, setDashboard] = useState(null);
  const [charges, setCharges] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [validations, setValidations] = useState([]);
  const [users, setUsers] = useState([]);
  const [delinquency, setDelinquency] = useState({ summary: { delinquent_count: 0, total_owed: 0 }, items: [] });
  const [showModal, setShowModal] = useState(null); // 'monthly' | 'category' | 'individual' | 'expense' | 'income'
  const [previewProof, setPreviewProof] = useState(null);

  const loadAll = async () => {
    const [d, c, e, i, v, u, dlq] = await Promise.all([
      api.get("/finance/dashboard"),
      api.get("/finance/charges"),
      api.get("/finance/expenses"),
      api.get("/finance/incomes"),
      api.get("/finance/validations").catch(() => ({ data: [] })),
      api.get("/users"),
      api.get("/finance/delinquency").catch(() => ({ data: { summary: { delinquent_count: 0, total_owed: 0 }, items: [] } })),
    ]);
    setDashboard(d.data);
    setCharges(c.data);
    setExpenses(e.data);
    setIncomes(i.data);
    setValidations(v.data);
    setUsers(u.data);
    setDelinquency(dlq.data);
  };
  useEffect(() => { loadAll(); }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.user_id, u])), [users]);

  const removeCharge = async (id) => {
    if (!confirm("¿Eliminar este cobro?")) return;
    await api.delete(`/finance/charges/${id}`);
    toast.success("Cobro eliminado");
    loadAll();
  };

  const removeExpense = async (id) => {
    if (!confirm("¿Eliminar este egreso?")) return;
    await api.delete(`/finance/expenses/${id}`);
    toast.success("Egreso eliminado");
    loadAll();
  };

  const removeIncome = async (id) => {
    if (!confirm("¿Eliminar este ingreso?")) return;
    await api.delete(`/finance/incomes/${id}`);
    toast.success("Ingreso eliminado");
    loadAll();
  };

  const validate = async (id, approved) => {
    let reason = null;
    if (!approved) {
      reason = prompt("Motivo del rechazo:");
      if (!reason?.trim()) return;
    }
    try {
      await api.post(`/finance/charges/${id}/validate`, { approved, reason });
      toast.success(approved ? "Pago aprobado" : "Pago rechazado");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1600px]">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline">Tesorería</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">FINANZAS</h1>
            <p className="text-[#A1A1AA] mt-2 text-sm">
              {canManage ? "Control de caja, cobros y egresos del club." : "Vista de solo lectura del flujo financiero del club."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => exportCashFlow({ charges, incomes, expenses, userMap })}
              className="btn-ghost text-xs flex items-center gap-1.5 border border-[#FACC15] text-[#FACC15]"
              data-testid="export-cashflow"
              title="Exportar todo el flujo de caja (cobros pagados + ingresos + egresos) a un solo CSV"
            >
              <FileSpreadsheet size={12}/> Exportar flujo de caja
            </button>
            {canManage && (
              <>
                <button onClick={() => setShowModal("monthly")} className="btn-primary text-xs flex items-center gap-1.5" data-testid="open-monthly-charge">
                  <UsersIcon size={12}/> Cuota mensual masiva
                </button>
                <button onClick={() => setShowModal("category")} className="btn-ghost text-xs flex items-center gap-1.5 border border-[#FACC15] text-[#FACC15]" data-testid="open-category-charge">
                  <Tag size={12}/> Cobro por categoría
                </button>
                <button onClick={() => setShowModal("individual")} className="btn-ghost text-xs flex items-center gap-1.5 border border-[#007AFF] text-[#007AFF]" data-testid="open-individual-charge">
                  <UserIcon size={12}/> Cobro individual
                </button>
                <button onClick={() => setShowModal("expense")} className="btn-ghost text-xs flex items-center gap-1.5 border border-[#FF3B30] text-[#FF3B30]" data-testid="open-expense">
                  <TrendingDown size={12}/> Registrar egreso
                </button>
                <button onClick={() => setShowModal("income")} className="btn-ghost text-xs flex items-center gap-1.5 border border-[#34C759] text-[#34C759]" data-testid="open-income">
                  <PiggyBank size={12}/> Registrar ingreso
                </button>
              </>
            )}
          </div>
        </div>

        {/* Dashboard */}
        <Dashboard dashboard={dashboard} validationsCount={validations.length}/>

        {/* Tabs */}
        <div className="flex border border-[#27272A] mb-4 mt-6 overflow-x-auto">
          <TabBtn active={tab === "charges"} onClick={() => setTab("charges")} testid="tab-charges">Cobros ({charges.length})</TabBtn>
          <TabBtn active={tab === "expenses"} onClick={() => setTab("expenses")} testid="tab-expenses">Egresos ({expenses.length})</TabBtn>
          <TabBtn active={tab === "incomes"} onClick={() => setTab("incomes")} testid="tab-incomes">Ingresos ({incomes.length})</TabBtn>
          <TabBtn
            active={tab === "validations"}
            onClick={() => setTab("validations")}
            testid="tab-validations"
            highlight={validations.length > 0}
          >
            Pagos por validar ({validations.length})
          </TabBtn>
          <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings">Configuración</TabBtn>
          <TabBtn
            active={tab === "delinquency"}
            onClick={() => setTab("delinquency")}
            testid="tab-delinquency"
            highlight={delinquency.items.length > 0}
          >
            Morosos ({delinquency.summary?.delinquent_count || 0})
          </TabBtn>
        </div>

        {tab === "charges" && <ChargesTable charges={charges} userMap={userMap} canManage={canManage} onDelete={removeCharge}/>}
        {tab === "expenses" && <ExpensesTable expenses={expenses} userMap={userMap} canManage={canManage} onDelete={removeExpense}/>}
        {tab === "incomes" && <IncomesTable incomes={incomes} userMap={userMap} canManage={canManage} onDelete={removeIncome}/>}
        {tab === "validations" && <ValidationsList items={validations} canManage={canManage} onValidate={validate} onPreview={setPreviewProof}/>}
        {tab === "settings" && <PaymentInfoSettings canManage={canManage}/>}
        {tab === "delinquency" && <DelinquencyPanel data={delinquency} canManage={canManage} onChanged={loadAll}/>}

        {showModal === "monthly" && <BulkMonthlyModal onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); loadAll(); }}/>}
        {showModal === "category" && <BulkCategoryModal onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); loadAll(); }}/>}
        {showModal === "individual" && <IndividualModal users={users.filter(u => u.role !== "pending")} onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); loadAll(); }}/>}
        {showModal === "expense" && <ExpenseModal onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); loadAll(); }}/>}
        {showModal === "income" && <IncomeModal onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); loadAll(); }}/>}
        {previewProof && <ProofPreviewModal proof={previewProof} onClose={() => setPreviewProof(null)}/>}

        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

// =================== DASHBOARD ===================
function Dashboard({ dashboard, validationsCount }) {
  return (
    <div className="space-y-4" data-testid="finance-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-0 border border-[#27272A]">
        <StatCell label="Ingresos totales" value={fmtMoney(dashboard?.income_total)} sub={`Cuotas + externos`} color="#34C759" icon={TrendingUp}/>
        <StatCell label="Cuotas pagadas" value={fmtMoney(dashboard?.dues_income_total)} sub={`${dashboard?.dues_income_count ?? 0} pagos`} color="#34C759" icon={Wallet}/>
        <StatCell label="Ingresos externos" value={fmtMoney(dashboard?.other_income_total)} sub={`${dashboard?.other_income_count ?? 0} registros`} color="#22d3ee" icon={PiggyBank}/>
        <StatCell label="Egresos" value={fmtMoney(dashboard?.expense_total)} color="#FF3B30" icon={TrendingDown}/>
        <StatCell label="Saldo neto" value={fmtMoney(dashboard?.balance)} color={dashboard?.balance >= 0 ? "#FACC15" : "#FF3B30"} icon={Wallet}/>
      </div>
      {validationsCount > 0 && (
        <div className="tactical-card p-3 flex items-center gap-3" style={{ borderColor: "#A78BFA", background: "linear-gradient(90deg, rgba(167,139,250,0.08) 0%, transparent 60%)" }} data-testid="validation-alert">
          <ClipboardCheck size={16} className="text-[#A78BFA]"/>
          <span className="text-sm text-white">
            <span className="font-bold">{validationsCount}</span> pago{validationsCount > 1 ? "s" : ""} esperando tu validación
          </span>
          <span className="text-[#A78BFA] text-xs uppercase tracking-widest ml-auto">→ pestaña "Pagos por validar"</span>
        </div>
      )}
      {dashboard?.timeline?.length > 0 && (
        <div className="tactical-card p-4">
          <div className="overline mb-3">Últimos 6 meses · ingresos vs egresos</div>
          <Timeline points={dashboard.timeline}/>
        </div>
      )}
    </div>
  );
}

const StatCell = ({ label, value, sub, color, icon: Icon }) => (
  <div className="p-5 border-r border-b border-[#27272A] last:border-r-0">
    <div className="flex items-center justify-between">
      <div className="overline text-[#71717A]">{label}</div>
      {Icon && <Icon size={14} className="text-[#71717A]"/>}
    </div>
    <div className="font-display text-3xl md:text-4xl mt-2" style={{ color: color || "#fff" }}>{value}</div>
    {sub && <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] mt-1">{sub}</div>}
  </div>
);

function Timeline({ points }) {
  const max = Math.max(1, ...points.flatMap(p => [p.income, p.expense]));
  return (
    <div className="space-y-2">
      {points.map(p => {
        const incW = (p.income / max) * 100;
        const expW = (p.expense / max) * 100;
        return (
          <div key={p.month} className="grid grid-cols-[60px_1fr_120px] items-center gap-2">
            <div className="font-mono text-xs text-[#A1A1AA]">{p.month}</div>
            <div className="space-y-1">
              <div className="h-2 bg-[#0A0A0A] relative">
                <div className="h-2 bg-[#34C759]" style={{ width: `${incW}%` }}/>
              </div>
              <div className="h-2 bg-[#0A0A0A] relative">
                <div className="h-2 bg-[#FF3B30]" style={{ width: `${expW}%` }}/>
              </div>
            </div>
            <div className="text-right text-[10px] font-mono">
              <div className="text-[#34C759]">{fmtMoney(p.income)}</div>
              <div className="text-[#FF3B30]">-{fmtMoney(p.expense)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =================== TABLES ===================
// =================== CASH FLOW EXPORT ===================
// Combines paid charges (dues_income), external incomes and expenses into a
// single chronological CSV stream — perfect for monthly reports to the board.
function exportCashFlow({ charges, incomes, expenses, userMap }) {
  const rows = [];
  // Paid charges → ingreso
  (charges || []).filter(c => c.status === "paid").forEach(c => rows.push({
    date: c.validated_at || c.created_at || "",
    type: "INGRESO",
    subtype: "Cuota",
    category: c.kind || "",
    source: userMap[c.user_id]?.name || c.user_id || "",
    description: c.concept || "",
    amount: c.amount,
    sign: +1,
  }));
  // External incomes → ingreso
  (incomes || []).forEach(i => rows.push({
    date: i.date || i.created_at || "",
    type: "INGRESO",
    subtype: "Externo",
    category: INCOME_LABEL[i.category] || i.category || "",
    source: i.source || "",
    description: i.description || "",
    amount: i.amount,
    sign: +1,
  }));
  // Expenses → egreso
  (expenses || []).forEach(e => rows.push({
    date: e.date || e.created_at || "",
    type: "EGRESO",
    subtype: "",
    category: e.category || "",
    source: userMap[e.created_by]?.name || "",
    description: e.description || "",
    amount: e.amount,
    sign: -1,
  }));
  // Sort oldest → newest with running balance
  rows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  let balance = 0;
  rows.forEach(r => { balance += r.sign * (Number(r.amount) || 0); r.balance = balance; });

  downloadCsv("flujo_de_caja", [
    { key: "date",        label: "Fecha",       format: r => fmtDate(r.date) },
    { key: "type",        label: "Tipo" },
    { key: "subtype",     label: "Subtipo" },
    { key: "category",    label: "Categoría" },
    { key: "source",      label: "Origen / Jugador" },
    { key: "description", label: "Descripción" },
    { key: "amount",      label: "Monto", format: r => r.sign * (Number(r.amount) || 0) },
    { key: "balance",     label: "Saldo acumulado" },
  ], rows);
}


const MONTH_LABELS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
function fmtMonthLabel(ym) {
  if (!ym || typeof ym !== "string" || ym.length < 7) return ym || "";
  const y = ym.slice(0, 4);
  const m = parseInt(ym.slice(5, 7), 10);
  if (!m || m < 1 || m > 12) return ym;
  return `${MONTH_LABELS_ES[m - 1]} ${y}`;
}


// =================== FILTER BAR ===================
function useFilter(items, { searchFrom, monthFrom }) {
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");

  const months = useMemo(() => {
    if (!monthFrom) return [];
    const set = new Set();
    items.forEach(r => { const m = monthFrom(r); if (m) set.add(m); });
    return Array.from(set).sort().reverse();
  }, [items, monthFrom]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter(r => {
      if (month && monthFrom && monthFrom(r) !== month) return false;
      if (!needle) return true;
      const hay = (searchFrom(r) || "").toLowerCase();
      return hay.includes(needle);
    });
  }, [items, q, month, searchFrom, monthFrom]);

  return { q, setQ, month, setMonth, months, filtered };
}

// =================== SORT + PAGINATION ===================
// Sort helper. `columns` is a map: { key: (row) => primitive }. `dir` ∈ {"asc","desc"}.
function useSort(items, columns, initialKey = null, initialDir = "desc") {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const sorted = useMemo(() => {
    if (!sortKey || !columns[sortKey]) return items;
    const getter = columns[sortKey];
    const dir = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = getter(a); const vb = getter(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [items, sortKey, sortDir, columns]);
  return { sortKey, sortDir, toggleSort, sorted };
}

function SortableTh({ label, sortKey, current, dir, onSort, align = "left", testid }) {
  const active = current === sortKey;
  const arrow = !active ? "↕" : (dir === "asc" ? "↑" : "↓");
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`p-3 overline cursor-pointer select-none hover:text-white transition-colors text-${align} ${active ? "text-white" : "text-[#A1A1AA]"}`}
      data-testid={testid}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[9px] ${active ? "text-[#007AFF]" : "text-[#52525B]"}`}>{arrow}</span>
      </span>
    </th>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function usePagination(items) {
  const [rawPage, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamp page during render (no useEffect → no double render, no stale UI).
  const page = Math.min(Math.max(1, rawPage), totalPages);
  const start = total === 0 ? 0 : (page - 1) * pageSize;
  const end = Math.min(total, start + pageSize);
  const pageItems = useMemo(() => items.slice(start, end), [items, start, end]);
  return { page, setPage, pageSize, setPageSize, total, totalPages, start, end, pageItems };
}

function Pagination({ page, setPage, pageSize, setPageSize, total, totalPages, start, end, testid }) {
  if (total === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 mt-3 px-1" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#71717A]">
        <span>Mostrar</span>
        <select
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          className="input-tactical w-auto py-1 text-xs"
          data-testid={`${testid}-size`}
        >
          {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>por página</span>
      </div>
      <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]" data-testid={`${testid}-info`}>
        Mostrando <strong className="text-white">{total === 0 ? 0 : start + 1}</strong> a <strong className="text-white">{end}</strong> de <strong className="text-white">{total}</strong> registros
      </span>
      <div className="flex items-center gap-1 ml-auto">
        <button
          type="button"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="btn-ghost text-[10px] uppercase tracking-widest border border-[#27272A] px-3 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`${testid}-prev`}
        >
          ← Anterior
        </button>
        <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] px-2" data-testid={`${testid}-page`}>
          Página <strong className="text-white">{page}</strong> de <strong className="text-white">{totalPages}</strong>
        </span>
        <button
          type="button"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className="btn-ghost text-[10px] uppercase tracking-widest border border-[#27272A] px-3 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`${testid}-next`}
        >
          Siguiente →
        </button>
      </div>
    </div>
  );
}

function FilterBar({ q, setQ, month, setMonth, months, total, filtered, placeholder, testid, onExport, exportDisabled }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={testid}>
      <div className="relative flex-1 min-w-[220px]">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]"/>
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={placeholder || "Buscar..."}
          className="input-tactical w-full pl-8"
          data-testid={`${testid}-search`}
        />
      </div>
      {months.length > 0 && (
        <select
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="input-tactical w-auto"
          data-testid={`${testid}-month`}
        >
          <option value="">Todos los meses</option>
          {months.map(m => <option key={m} value={m}>{fmtMonthLabel(m)}</option>)}
        </select>
      )}
      {(q || month) && (
        <button
          type="button"
          onClick={() => { setQ(""); setMonth(""); }}
          className="btn-ghost text-[10px] uppercase tracking-widest border border-[#27272A]"
          data-testid={`${testid}-clear`}
        >
          Limpiar
        </button>
      )}
      {onExport && (
        <button
          type="button"
          onClick={onExport}
          disabled={exportDisabled}
          className="btn-ghost text-[10px] uppercase tracking-widest border border-[#34C759] text-[#34C759] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-export`}
          title="Exportar visible a CSV (Excel)"
        >
          <Download size={11}/> Exportar CSV
        </button>
      )}
      <span className="text-[10px] uppercase tracking-widest text-[#71717A] ml-auto">
        {filtered} / {total}
      </span>
    </div>
  );
}

function ChargesTable({ charges, userMap, canManage, onDelete }) {
  const monthOf = (c) => (c.created_at || "").slice(0, 7);
  const hayOf = (c) => `${userMap[c.user_id]?.name || ""} ${c.concept || ""} ${c.kind || ""} ${c.status || ""}`;
  const f = useFilter(charges, { searchFrom: hayOf, monthFrom: monthOf });
  const sortCols = useMemo(() => ({
    player:  c => userMap[c.user_id]?.name || c.user_id,
    concept: c => c.concept || "",
    amount:  c => Number(c.amount || 0),
    status:  c => c.status || "",
    created: c => c.created_at || "",
  }), [userMap]);
  const s = useSort(f.filtered, sortCols, "created", "desc");
  const p = usePagination(s.sorted);
  const exportCsv = () => {
    downloadCsv("cobros", [
      { key: "user_id", label: "Jugador", format: c => userMap[c.user_id]?.name || c.user_id },
      { key: "user_id", label: "Email",   format: c => userMap[c.user_id]?.email || "" },
      { key: "concept", label: "Concepto" },
      { key: "kind",    label: "Tipo" },
      { key: "amount",  label: "Monto" },
      { key: "status",  label: "Estado", format: c => (STATUS_LABEL[c.status] || {}).label || c.status },
      { key: "created_at", label: "Creado", format: c => fmtDate(c.created_at) },
      { key: "rejection_reason", label: "Motivo rechazo" },
    ], s.sorted);
  };
  return (
    <div>
      <FilterBar
        q={f.q} setQ={f.setQ} month={f.month} setMonth={f.setMonth} months={f.months}
        total={charges.length} filtered={f.filtered.length}
        placeholder="Buscar por jugador, concepto, estado..."
        testid="charges-filter"
        onExport={exportCsv}
        exportDisabled={s.sorted.length === 0}
      />
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A] text-left">
              <SortableTh label="Jugador"  sortKey="player"  current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ch-sort-player"/>
              <SortableTh label="Concepto" sortKey="concept" current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ch-sort-concept"/>
              <th className="text-left p-3 overline">Tipo</th>
              <SortableTh label="Monto"    sortKey="amount"  current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ch-sort-amount"/>
              <SortableTh label="Estado"   sortKey="status"  current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ch-sort-status"/>
              <SortableTh label="Creado"   sortKey="created" current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ch-sort-created"/>
              {canManage && <th className="text-right p-3 overline">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {p.pageItems.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-[#71717A]">{charges.length === 0 ? "No hay cobros registrados." : "Sin resultados para el filtro."}</td></tr>
            )}
            {p.pageItems.map(c => {
              const st = STATUS_LABEL[c.status] || STATUS_LABEL.pending;
              return (
                <tr key={c.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`charge-row-${c.id}`}>
                  <td className="p-3 text-white">{userMap[c.user_id]?.name || c.user_id}</td>
                  <td className="p-3 text-white">{c.concept}</td>
                  <td className="p-3 text-[10px] uppercase tracking-wider text-[#A1A1AA]">{c.kind.replace("_", " ")}</td>
                  <td className="p-3 font-mono text-white">{fmtMoney(c.amount)}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${st.cls}`}>{st.label}</span></td>
                  <td className="p-3 text-xs text-[#A1A1AA]">{fmtDate(c.created_at)}</td>
                  {canManage && (
                    <td className="p-3 text-right">
                      <button onClick={() => onDelete(c.id)} className="text-[#71717A] hover:text-[#FF3B30]" data-testid={`del-charge-${c.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination {...p} testid="charges-pagination"/>
    </div>
  );
}

function ExpensesTable({ expenses, userMap, canManage, onDelete }) {
  const monthOf = (e) => (e.date || "").slice(0, 7);
  const hayOf = (e) => `${e.category || ""} ${e.description || ""} ${userMap[e.created_by]?.name || ""}`;
  const f = useFilter(expenses, { searchFrom: hayOf, monthFrom: monthOf });
  const sortCols = useMemo(() => ({
    date:        e => e.date || "",
    category:    e => e.category || "",
    description: e => e.description || "",
    amount:      e => Number(e.amount || 0),
    createdBy:   e => userMap[e.created_by]?.name || "",
  }), [userMap]);
  const s = useSort(f.filtered, sortCols, "date", "desc");
  const p = usePagination(s.sorted);
  const exportCsv = () => {
    downloadCsv("egresos", [
      { key: "date", label: "Fecha", format: e => fmtDate(e.date) },
      { key: "category", label: "Categoría" },
      { key: "description", label: "Descripción" },
      { key: "amount", label: "Monto" },
      { key: "created_by", label: "Registrado por", format: e => userMap[e.created_by]?.name || "" },
    ], s.sorted);
  };
  return (
    <div>
      <FilterBar
        q={f.q} setQ={f.setQ} month={f.month} setMonth={f.setMonth} months={f.months}
        total={expenses.length} filtered={f.filtered.length}
        placeholder="Buscar por categoría, descripción..."
        testid="expenses-filter"
        onExport={exportCsv}
        exportDisabled={s.sorted.length === 0}
      />
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A] text-left">
              <SortableTh label="Fecha"       sortKey="date"        current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ex-sort-date"/>
              <SortableTh label="Categoría"   sortKey="category"    current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ex-sort-cat"/>
              <SortableTh label="Descripción" sortKey="description" current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ex-sort-desc"/>
              <SortableTh label="Monto"       sortKey="amount"      current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ex-sort-amount"/>
              <SortableTh label="Registrado por" sortKey="createdBy" current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="ex-sort-by"/>
              {canManage && <th className="text-right p-3 overline">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {p.pageItems.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-[#71717A]">{expenses.length === 0 ? "No hay egresos registrados." : "Sin resultados para el filtro."}</td></tr>
            )}
            {p.pageItems.map(e => (
              <tr key={e.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`expense-row-${e.id}`}>
                <td className="p-3 font-mono text-xs text-white">{fmtDate(e.date)}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#27272A] text-[#A1A1AA]">{e.category}</span></td>
                <td className="p-3 text-white">{e.description || "—"}</td>
                <td className="p-3 font-mono text-[#FF3B30]">-{fmtMoney(e.amount)}</td>
                <td className="p-3 text-xs text-[#A1A1AA]">{userMap[e.created_by]?.name || "—"}</td>
                {canManage && (
                  <td className="p-3 text-right">
                    <button onClick={() => onDelete(e.id)} className="text-[#71717A] hover:text-[#FF3B30]" data-testid={`del-expense-${e.id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...p} testid="expenses-pagination"/>
    </div>
  );
}

function IncomesTable({ incomes, userMap, canManage, onDelete }) {
  const monthOf = (i) => (i.date || "").slice(0, 7);
  const hayOf = (i) => `${INCOME_LABEL[i.category] || i.category || ""} ${i.source || ""} ${i.description || ""}`;
  const f = useFilter(incomes, { searchFrom: hayOf, monthFrom: monthOf });
  const sortCols = useMemo(() => ({
    date:        i => i.date || "",
    category:    i => INCOME_LABEL[i.category] || i.category || "",
    source:      i => i.source || "",
    description: i => i.description || "",
    amount:      i => Number(i.amount || 0),
    createdBy:   i => userMap[i.created_by]?.name || "",
  }), [userMap]);
  const s = useSort(f.filtered, sortCols, "date", "desc");
  const p = usePagination(s.sorted);
  const exportCsv = () => {
    downloadCsv("ingresos", [
      { key: "date", label: "Fecha", format: i => fmtDate(i.date) },
      { key: "category", label: "Tipo", format: i => INCOME_LABEL[i.category] || i.category },
      { key: "source", label: "Origen" },
      { key: "description", label: "Descripción" },
      { key: "amount", label: "Monto" },
      { key: "created_by", label: "Registrado por", format: i => userMap[i.created_by]?.name || "" },
    ], s.sorted);
  };
  return (
    <div>
      <FilterBar
        q={f.q} setQ={f.setQ} month={f.month} setMonth={f.setMonth} months={f.months}
        total={incomes.length} filtered={f.filtered.length}
        placeholder="Buscar por origen, categoría, descripción..."
        testid="incomes-filter"
        onExport={exportCsv}
        exportDisabled={s.sorted.length === 0}
      />
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A] text-left">
              <SortableTh label="Fecha"       sortKey="date"        current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="in-sort-date"/>
              <SortableTh label="Tipo"        sortKey="category"    current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="in-sort-cat"/>
              <SortableTh label="Origen"      sortKey="source"      current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="in-sort-src"/>
              <SortableTh label="Descripción" sortKey="description" current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="in-sort-desc"/>
              <SortableTh label="Monto"       sortKey="amount"      current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="in-sort-amount"/>
              <SortableTh label="Registrado por" sortKey="createdBy" current={s.sortKey} dir={s.sortDir} onSort={s.toggleSort} testid="in-sort-by"/>
              {canManage && <th className="text-right p-3 overline">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {p.pageItems.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-[#71717A]">{incomes.length === 0 ? "Aún no hay ingresos externos registrados. Usa &quot;Registrar ingreso&quot; para sumar fondos concursables, donaciones, sponsors, etc." : "Sin resultados para el filtro."}</td></tr>
            )}
            {p.pageItems.map(i => (
              <tr key={i.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`income-row-${i.id}`}>
                <td className="p-3 font-mono text-xs text-white">{fmtDate(i.date)}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#34C759] text-[#34C759]">{INCOME_LABEL[i.category] || i.category}</span></td>
                <td className="p-3 text-white">{i.source || "—"}</td>
                <td className="p-3 text-[#A1A1AA]">{i.description || "—"}</td>
                <td className="p-3 font-mono text-[#34C759]">+{fmtMoney(i.amount)}</td>
                <td className="p-3 text-xs text-[#A1A1AA]">{userMap[i.created_by]?.name || "—"}</td>
                {canManage && (
                  <td className="p-3 text-right">
                    <button onClick={() => onDelete(i.id)} className="text-[#71717A] hover:text-[#FF3B30]" data-testid={`del-income-${i.id}`}>
                      <Trash2 size={14}/>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination {...p} testid="incomes-pagination"/>
    </div>
  );
}

function ValidationsList({ items, canManage, onValidate, onPreview }) {
  const monthOf = (c) => (c.payment_proof?.informed_at || c.created_at || "").slice(0, 7);
  const hayOf = (c) => `${c.user_name || ""} ${c.user_email || ""} ${c.concept || ""} ${c.payment_proof?.transfer_ref || ""}`;
  const f = useFilter(items, { searchFrom: hayOf, monthFrom: monthOf });
  const p = usePagination(f.filtered);
  const exportCsv = () => {
    downloadCsv("pagos_por_validar", [
      { key: "user_name", label: "Jugador" },
      { key: "user_email", label: "Email" },
      { key: "concept", label: "Concepto" },
      { key: "amount", label: "Monto" },
      { key: "transfer_ref", label: "Nº transferencia", format: c => c.payment_proof?.transfer_ref || "" },
      { key: "informed_at", label: "Informado", format: c => fmtDate(c.payment_proof?.informed_at) },
    ], f.filtered);
  };
  return (
    <div>
      <FilterBar
        q={f.q} setQ={f.setQ} month={f.month} setMonth={f.setMonth} months={f.months}
        total={items.length} filtered={f.filtered.length}
        placeholder="Buscar por jugador, concepto, Nº transferencia..."
        testid="validations-filter"
        onExport={exportCsv}
        exportDisabled={f.filtered.length === 0}
      />
      {f.filtered.length === 0 ? (
        <div className="tactical-card p-10 text-center text-[#71717A]" data-testid="no-validations">
          {items.length === 0 ? "No hay pagos pendientes de validación." : "Sin resultados para el filtro."}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {p.pageItems.map(c => (
              <div key={c.id} className="tactical-card p-4" data-testid={`validation-${c.id}`} style={{ borderColor: "#A78BFA" }}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-display text-lg text-white">{c.concept}</span>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#A78BFA] text-[#A78BFA]">{STATUS_LABEL.pending_validation.label}</span>
                    </div>
                    <div className="text-sm text-[#A1A1AA]">
                      <span className="font-bold text-white">{c.user_name}</span> · {c.user_email} · informado {fmtDate(c.payment_proof?.informed_at)}
                    </div>
                    <div className="text-xs text-[#71717A] mt-1">
                      Nº transferencia: <span className="font-mono text-white">{c.payment_proof?.transfer_ref}</span>
                    </div>
                  </div>
                  <div className="font-display text-3xl text-[#FACC15]">{fmtMoney(c.amount)}</div>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-[#27272A] flex-wrap">
                  <button onClick={() => onPreview(c.payment_proof)} className="btn-ghost text-xs flex items-center gap-1.5" data-testid={`preview-proof-${c.id}`}>
                    <Eye size={12}/> Ver comprobante
                  </button>
                  {canManage && (
                    <>
                      <button onClick={() => onValidate(c.id, true)} className="btn-primary text-xs flex items-center gap-1.5" data-testid={`approve-${c.id}`}>
                        <CheckCircle2 size={12}/> Aprobar
                      </button>
                      <button onClick={() => onValidate(c.id, false)} className="btn-ghost text-xs text-[#FF3B30] border border-[#FF3B30] flex items-center gap-1.5" data-testid={`reject-${c.id}`}>
                        <X size={12}/> Rechazar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination {...p} testid="validations-pagination"/>
        </>
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid, highlight }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 text-xs uppercase tracking-wider font-bold border-r border-[#27272A] last:border-r-0 transition-colors ${
      active ? "text-white bg-[#1A1A1A] border-b-2 border-b-[#007AFF]" : highlight ? "text-[#A78BFA] hover:bg-[#121212]" : "text-[#A1A1AA] hover:text-white"
    }`}
  >
    {children}
  </button>
);

// =================== MODALS ===================
function BulkMonthlyModal({ onClose, onSaved }) {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [data, setData] = useState({ month: defaultMonth, amount: "15000", concept: "", note: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/finance/charges/bulk_monthly", {
        month: data.month, amount: Number(data.amount), concept: data.concept || null, note: data.note,
      });
      toast.success(`${r.data.created} cuotas creadas · ${r.data.skipped} omitidas`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title="CUOTA MENSUAL MASIVA" subtitle="Se asignará a TODOS los usuarios activos del club" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="bulk-monthly-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mes / Año">
            <input
              required
              type="month"
              className="input-tactical"
              value={data.month}
              onChange={e => setData({ ...data, month: e.target.value })}
              data-testid="bm-month"
            />
          </Field>
          <Field label="Monto $">
            <input required type="number" min={0} className="input-tactical" value={data.amount} onChange={e => setData({ ...data, amount: e.target.value })} data-testid="bm-amount"/>
          </Field>
        </div>
        <div className="text-[11px] text-[#71717A] -mt-2">
          Vencimiento automático: <strong className="text-[#A1A1AA]">día 15 de {fmtMonthLabel(data.month)}</strong>
        </div>
        <Field label="Concepto (opcional)">
          <input className="input-tactical" placeholder={`Cuota ${fmtMonthLabel(data.month)}`} value={data.concept} onChange={e => setData({ ...data, concept: e.target.value })}/>
        </Field>
        <Field label="Nota (opcional)">
          <input className="input-tactical" value={data.note} onChange={e => setData({ ...data, note: e.target.value })}/>
        </Field>
        <ModalActions onClose={onClose} saving={saving} submitLabel="GENERAR CUOTAS" testid="bm-submit"/>
      </form>
    </ModalShell>
  );
}

function BulkCategoryModal({ onClose, onSaved }) {
  const [data, setData] = useState({ categories: [], amount: "", concept: "", note: "", kind: "extraordinary" });
  const [saving, setSaving] = useState(false);
  const toggle = (c) => setData(d => ({ ...d, categories: d.categories.includes(c) ? d.categories.filter(x => x !== c) : [...d.categories, c] }));
  const submit = async (e) => {
    e.preventDefault();
    if (data.categories.length === 0) return toast.error("Selecciona al menos una categoría");
    if (!data.concept.trim()) return toast.error("Ingresa el concepto");
    setSaving(true);
    try {
      const r = await api.post("/finance/charges/bulk_category", {
        categories: data.categories, amount: Number(data.amount), concept: data.concept, note: data.note, kind: data.kind,
      });
      toast.success(`${r.data.created} cobros creados`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title="COBRO MASIVO POR CATEGORÍA" subtitle="Solo a usuarios con perfil en la(s) categoría(s)" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="bulk-cat-form">
        <Field label="Categorías a cobrar">
          <div className="flex flex-wrap gap-2">
            {CAT_KEYS.map(c => {
              const active = data.categories.includes(c);
              return (
                <button type="button" key={c} onClick={() => toggle(c)} data-testid={`bc-cat-${c}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border ${active ? "border-[#FACC15] bg-[#1A1A1A] text-white" : "border-[#27272A] text-[#A1A1AA]"}`}>
                  {CATEGORY_LABEL[c]}
                </button>
              );
            })}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Concepto"><input required className="input-tactical" placeholder="Ej: Inscripción Torneo X" value={data.concept} onChange={e => setData({ ...data, concept: e.target.value })} data-testid="bc-concept"/></Field>
          <Field label="Monto $"><input required type="number" min={0} className="input-tactical" value={data.amount} onChange={e => setData({ ...data, amount: e.target.value })} data-testid="bc-amount"/></Field>
        </div>
        <Field label="Tipo">
          <select className="input-tactical" value={data.kind} onChange={e => setData({ ...data, kind: e.target.value })}>
            <option value="enrollment">Inscripción</option>
            <option value="extraordinary">Extraordinario</option>
            <option value="jersey">Jersey</option>
            <option value="fine">Multa</option>
            <option value="other">Otro</option>
          </select>
        </Field>
        <Field label="Nota (opcional)"><input className="input-tactical" value={data.note} onChange={e => setData({ ...data, note: e.target.value })}/></Field>
        <ModalActions onClose={onClose} saving={saving} submitLabel="CREAR COBROS" testid="bc-submit"/>
      </form>
    </ModalShell>
  );
}

function IndividualModal({ users, onClose, onSaved }) {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [data, setData] = useState({ user_id: "", amount: "", concept: "", note: "", kind: "individual", month: defaultMonth });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/finance/charges/individual", {
        user_id: data.user_id, amount: Number(data.amount), concept: data.concept, note: data.note, kind: data.kind, month: data.month || null,
      });
      toast.success("Cobro creado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title="COBRO INDIVIDUAL" subtitle="Asigna un cobro a un único usuario" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="ind-form">
        <Field label="Usuario">
          <select required className="input-tactical" value={data.user_id} onChange={e => setData({ ...data, user_id: e.target.value })} data-testid="ind-user">
            <option value="">Selecciona...</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mes / Año">
            <input
              required
              type="month"
              className="input-tactical"
              value={data.month}
              onChange={e => setData({ ...data, month: e.target.value })}
              data-testid="ind-month"
            />
          </Field>
          <Field label="Monto $"><input required type="number" min={0} className="input-tactical" value={data.amount} onChange={e => setData({ ...data, amount: e.target.value })} data-testid="ind-amount"/></Field>
        </div>
        <Field label="Concepto"><input required className="input-tactical" placeholder="Ej: Compra de jersey" value={data.concept} onChange={e => setData({ ...data, concept: e.target.value })} data-testid="ind-concept"/></Field>
        <Field label="Tipo">
          <select className="input-tactical" value={data.kind} onChange={e => setData({ ...data, kind: e.target.value })} data-testid="ind-kind">
            <option value="monthly_dues">Cuota mensual</option>
            <option value="enrollment">Inscripción</option>
            <option value="individual">Individual</option>
            <option value="jersey">Jersey</option>
            <option value="fine">Multa</option>
            <option value="extraordinary">Extraordinario</option>
            <option value="other">Otro</option>
          </select>
        </Field>
        {data.kind === "monthly_dues" && (
          <div className="text-[11px] text-[#71717A] -mt-2">
            Vencimiento automático: <strong className="text-[#A1A1AA]">día 15 de {fmtMonthLabel(data.month)}</strong>
          </div>
        )}
        <Field label="Nota (opcional)"><input className="input-tactical" value={data.note} onChange={e => setData({ ...data, note: e.target.value })}/></Field>
        <ModalActions onClose={onClose} saving={saving} submitLabel="CREAR COBRO" testid="ind-submit"/>
      </form>
    </ModalShell>
  );
}

function ExpenseModal({ onClose, onSaved }) {
  const today = todayLocalIso();
  const [data, setData] = useState({ amount: "", date: today, category: "balones", description: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/finance/expenses", { ...data, amount: Number(data.amount) });
      toast.success("Egreso registrado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title="REGISTRAR EGRESO" subtitle="Gasto del club (sale de la caja)" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha"><input required type="date" className="input-tactical" value={data.date} onChange={e => setData({ ...data, date: e.target.value })} data-testid="ex-date"/></Field>
          <Field label="Monto $"><input required type="number" min={0} className="input-tactical" value={data.amount} onChange={e => setData({ ...data, amount: e.target.value })} data-testid="ex-amount"/></Field>
        </div>
        <Field label="Categoría">
          <select className="input-tactical" value={data.category} onChange={e => setData({ ...data, category: e.target.value })} data-testid="ex-cat">
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Descripción"><input className="input-tactical" placeholder="Ej: 5 balones nuevos" value={data.description} onChange={e => setData({ ...data, description: e.target.value })}/></Field>
        <ModalActions onClose={onClose} saving={saving} submitLabel="REGISTRAR" testid="ex-submit"/>
      </form>
    </ModalShell>
  );
}

function IncomeModal({ onClose, onSaved }) {
  const today = todayLocalIso();
  const [data, setData] = useState({
    amount: "", date: today, category: "fondo_concursable", source: "", description: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (Number(data.amount) <= 0) return toast.error("El monto debe ser mayor a 0");
    setSaving(true);
    try {
      await api.post("/finance/incomes", { ...data, amount: Number(data.amount) });
      toast.success("Ingreso registrado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title="REGISTRAR INGRESO" subtitle="Fondo concursable, donación, sponsor u otro ingreso externo" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="income-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha">
            <input required type="date" className="input-tactical" value={data.date}
              onChange={e => setData({ ...data, date: e.target.value })} data-testid="in-date"/>
          </Field>
          <Field label="Monto $">
            <input required type="number" min={1} className="input-tactical" value={data.amount}
              onChange={e => setData({ ...data, amount: e.target.value })} data-testid="in-amount"/>
          </Field>
        </div>
        <Field label="Tipo de ingreso">
          <select className="input-tactical" value={data.category}
            onChange={e => setData({ ...data, category: e.target.value })} data-testid="in-cat">
            {INCOME_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Origen / Fuente">
          <input className="input-tactical"
            placeholder="Ej: Municipalidad de Puente Alto, Empresa Acme, Familia Pérez"
            value={data.source} onChange={e => setData({ ...data, source: e.target.value })}
            data-testid="in-source"/>
        </Field>
        <Field label="Descripción">
          <input className="input-tactical"
            placeholder="Ej: Subvención anual 2026 - Programa Mejor Niñez"
            value={data.description} onChange={e => setData({ ...data, description: e.target.value })}
            data-testid="in-desc"/>
        </Field>
        <ModalActions onClose={onClose} saving={saving} submitLabel="REGISTRAR INGRESO" testid="in-submit"/>
      </form>
    </ModalShell>
  );
}

function ProofPreviewModal({ proof, onClose }) {
  // `proof` puede ser:
  //   - Nuevo (Drive):  { drive_file_id, drive_view_link, drive_file_name, mime_type, ... }
  //   - Legacy (OS):    { photo_path, ... }   ← retrocompat para registros viejos
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const driveId = proof?.drive_file_id || null;
  const legacyPath = proof?.photo_path || null;
  const externalLink = proof?.drive_view_link || null;
  const label = proof?.drive_file_name || proof?.photo_path || "Comprobante";

  useEffect(() => {
    let url;
    const fetcher = driveId
      ? api.get(`/drive/file/${driveId}`, { responseType: "blob" })
      : legacyPath
        ? api.get(`/files/${legacyPath}`, { responseType: "blob" })
        : Promise.reject(new Error("Sin comprobante"));
    fetcher
      .then(r => { url = URL.createObjectURL(r.data); setBlobUrl(url); })
      .catch(err => setError(err?.response?.data?.detail || "No se pudo cargar el comprobante"));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [driveId, legacyPath]);

  return (
    <ModalShell title="COMPROBANTE DE PAGO" subtitle={label} onClose={onClose} wide>
      <div className="bg-[#0A0A0A] border border-[#27272A] p-2 flex flex-col items-center justify-center min-h-[300px] gap-3" data-testid="proof-preview">
        {error ? (
          <div className="text-[#FF3B30] text-sm text-center px-6 py-10">{error}</div>
        ) : blobUrl ? (
          <img src={blobUrl} alt="Comprobante" className="max-w-full max-h-[65vh] object-contain"/>
        ) : (
          <div className="text-[#71717A] text-sm">Cargando...</div>
        )}
        {externalLink && (
          <a
            href={externalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost text-[10px] uppercase tracking-wider border border-cyan-400/60 text-cyan-300 px-3 py-1.5"
            data-testid="proof-open-drive"
          >
            Abrir en Drive
          </a>
        )}
      </div>
    </ModalShell>
  );
}

// =================== PLAYER VIEW ===================
function PlayerFinanceView() {
  const [data, setData] = useState(null);
  const [informingFor, setInformingFor] = useState(null);
  const [showPayInfo, setShowPayInfo] = useState(false);

  const load = () => api.get("/finance/charges/me").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const pendingCharges = useMemo(() => (data?.charges || []).filter(c => c.status === "pending" || c.status === "rejected"), [data]);
  const validationCharges = useMemo(() => (data?.charges || []).filter(c => c.status === "pending_validation"), [data]);
  const paidCharges = useMemo(() => (data?.charges || []).filter(c => c.status === "paid"), [data]);

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1200px]">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline">Finanzas</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">MIS CUOTAS</h1>
            <p className="text-[#A1A1AA] mt-2 text-sm">Estado de tu cuenta, cobros pendientes e historial.</p>
          </div>
          <button
            onClick={() => setShowPayInfo(true)}
            className="btn-primary text-xs flex items-center gap-1.5"
            data-testid="open-pay-info"
          >
            <CreditCard size={12}/> PAGAR · DATOS BANCARIOS
          </button>
        </div>

        {/* Debt summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#27272A] mb-6">
          <div className="p-5 border-r border-b border-[#27272A]" style={{ background: data?.pending_total > 0 ? "linear-gradient(120deg, rgba(255,159,10,0.08) 0%, transparent 60%)" : undefined }}>
            <div className="overline text-[#FF9F0A]">Deuda total</div>
            <div className="font-display text-4xl mt-1" style={{ color: data?.pending_total > 0 ? "#FF9F0A" : "#A1A1AA" }} data-testid="player-debt">{fmtMoney(data?.pending_total)}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">{data?.pending_count ?? 0} cobros pendientes</div>
          </div>
          <div className="p-5 border-r border-b border-[#27272A]">
            <div className="overline text-[#A78BFA]">Por validar</div>
            <div className="font-display text-4xl mt-1 text-[#A78BFA]">{data?.validation_count ?? 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">Esperando revisión del tesorero</div>
          </div>
          <div className="p-5 border-b border-[#27272A]">
            <div className="overline text-[#34C759]">Pagado total</div>
            <div className="font-display text-4xl mt-1 text-[#34C759]">{fmtMoney(data?.paid_total)}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">Histórico aprobado</div>
          </div>
        </div>

        {/* Pending */}
        <Section title="Cobros pendientes" icon={AlertCircle} color="#FF9F0A" testid="player-pending-section">
          {pendingCharges.length === 0 && <Empty>Sin cobros pendientes. ¡Estás al día!</Empty>}
          {pendingCharges.map(c => (
            <ChargeCard
              key={c.id}
              charge={c}
              actionLabel="Informar pago"
              onAction={() => setInformingFor(c)}
              actionIcon={Upload}
              secondaryLabel="Pagar"
              onSecondary={() => setShowPayInfo(true)}
              secondaryIcon={CreditCard}
            />
          ))}
        </Section>

        {/* Pending validation */}
        {validationCharges.length > 0 && (
          <Section title="Pagos informados (esperando validación)" icon={ClipboardCheck} color="#A78BFA" testid="player-validation-section">
            {validationCharges.map(c => (
              <ChargeCard key={c.id} charge={c} infoLine={`Nº ${c.payment_proof?.transfer_ref} · informado ${fmtDate(c.payment_proof?.informed_at)}`}/>
            ))}
          </Section>
        )}

        {/* Historial */}
        <Section title="Historial de pagos" icon={History} color="#34C759" testid="player-history-section">
          {paidCharges.length === 0 && <Empty>Aún no tienes pagos aprobados.</Empty>}
          {paidCharges.map(c => (
            <ChargeCard key={c.id} charge={c} infoLine={c.validated_at ? `Aprobado el ${fmtDate(c.validated_at)}` : null}/>
          ))}
        </Section>

        {informingFor && <InformPaymentModal charge={informingFor} onClose={() => setInformingFor(null)} onSaved={() => { setInformingFor(null); load(); }}/>}
        {showPayInfo && <PaymentInfoModal onClose={() => setShowPayInfo(false)}/>}

        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

const Section = ({ title, icon: Icon, color, children, testid }) => (
  <div className="mb-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={14} style={{ color }}/>
      <div className="overline" style={{ color }}>{title}</div>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Empty = ({ children }) => <div className="tactical-card p-6 text-center text-[#71717A] text-sm">{children}</div>;

function ChargeCard({ charge, actionLabel, onAction, actionIcon: ActionIcon, infoLine, secondaryLabel, onSecondary, secondaryIcon: SecondaryIcon }) {
  const st = STATUS_LABEL[charge.status];
  return (
    <div className="tactical-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`my-charge-${charge.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-lg text-white">{charge.concept}</span>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${st.cls}`}>{st.label}</span>
        </div>
        {charge.note && <div className="text-xs text-[#A1A1AA] mt-0.5">{charge.note}</div>}
        {infoLine && <div className="text-xs text-[#71717A] mt-0.5">{infoLine}</div>}
        {charge.status === "rejected" && charge.rejection_reason && (
          <div className="text-xs text-[#FF3B30] mt-0.5">Motivo: {charge.rejection_reason}. Vuelve a informar pago.</div>
        )}
      </div>
      <div className="font-display text-2xl text-white">{fmtMoney(charge.amount)}</div>
      <div className="flex items-center gap-2">
        {secondaryLabel && onSecondary && (
          <button
            onClick={onSecondary}
            className="btn-ghost text-xs flex items-center gap-1.5 border border-[#34C759] text-[#34C759]"
            data-testid={`pay-${charge.id}`}
          >
            {SecondaryIcon && <SecondaryIcon size={12}/>} {secondaryLabel}
          </button>
        )}
        {actionLabel && onAction && (
          <button onClick={onAction} className="btn-primary text-xs flex items-center gap-1.5" data-testid={`inform-${charge.id}`}>
            {ActionIcon && <ActionIcon size={12}/>} {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function InformPaymentModal({ charge, onClose, onSaved }) {
  const [data, setData] = useState({ transfer_ref: "", file: null });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!data.transfer_ref.trim()) return toast.error("Ingresa el nº de transferencia");
    if (!data.file) return toast.error("Adjunta una foto del comprobante");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", data.file);
      const qs = new URLSearchParams({ transfer_ref: data.transfer_ref.trim() });
      await api.post(`/finance/charges/${charge.id}/inform?${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Pago informado · esperando validación");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title="INFORMAR PAGO" subtitle={`${charge.concept} · ${fmtMoney(charge.amount)}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="inform-form">
        <div className="bg-[#1A1A1A] border-l-2 border-[#A78BFA] p-3">
          <div className="overline text-[#A78BFA] mb-1">Instrucciones</div>
          <div className="text-xs text-[#A1A1AA] leading-relaxed">
            Realiza la transferencia a la cuenta del club (ver datos en el modal de "Pagar" o pregúntale al tesorero). Luego ingresa el nº de transferencia y adjunta una foto/captura del comprobante.
          </div>
        </div>
        <Field label="Nº de transferencia *">
          <input required className="input-tactical font-mono" value={data.transfer_ref} onChange={e => setData({ ...data, transfer_ref: e.target.value })} data-testid="inf-ref"/>
        </Field>
        <Field label="Foto del comprobante *">
          <input required type="file" accept="image/*" className="input-tactical" onChange={e => setData({ ...data, file: e.target.files[0] })} data-testid="inf-file"/>
          <div className="text-[10px] text-[#71717A] mt-1 flex items-center gap-1"><FileImage size={10}/> JPG, PNG o screenshot</div>
        </Field>
        <ModalActions onClose={onClose} saving={saving} submitLabel="INFORMAR PAGO" testid="inf-submit"/>
      </form>
    </ModalShell>
  );
}

// =================== PAYMENT INFO SETTINGS (Admin tab) ===================
function PaymentInfoSettings({ canManage }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const FIELDS = [
    { key: "club_name", label: "Nombre del club", hint: "Aparece en el modal de pago." },
    { key: "bank", label: "Banco" },
    { key: "account_type", label: "Tipo de cuenta", hint: "Ej: Cuenta Corriente, Cuenta Vista, Cuenta RUT." },
    { key: "account_number", label: "Nº de cuenta", mono: true },
    { key: "rut", label: "RUT", mono: true },
    { key: "holder", label: "Titular" },
    { key: "email", label: "Email del club", hint: "Donde el jugador puede mandar dudas." },
    { key: "instructions", label: "Instrucciones para el jugador", textarea: true },
  ];

  useEffect(() => {
    api.get("/club/payment-info")
      .then(r => { setInfo(r.data); setForm(r.data); })
      .catch(() => toast.error("Error al cargar datos"))
      .finally(() => setLoading(false));
  }, []);

  const dirty = form && info && FIELDS.some(f => (form[f.key] || "") !== (info[f.key] || ""));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/club/payment-info", form);
      setInfo(r.data);
      setForm(r.data);
      toast.success("Datos bancarios actualizados");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="tactical-card p-10 text-center text-[#71717A] text-sm">Cargando...</div>;

  return (
    <div className="tactical-card p-6 space-y-5" data-testid="payment-settings">
      <div className="flex items-center gap-2">
        <Settings size={14} className="text-[#007AFF]"/>
        <div className="overline text-[#007AFF]">Datos bancarios del club</div>
      </div>
      <p className="text-xs text-[#A1A1AA] leading-relaxed">
        {canManage
          ? "Estos datos se muestran a los jugadores cuando aprietan \"Pagar\" en Mis Cuotas. Edita y guarda los cambios."
          : "Vista de solo lectura. Solo el tesorero o super admin puede editar."}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="payment-settings-form">
        {FIELDS.map(f => (
          <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
            <label className="label-tactical">{f.label}</label>
            {f.textarea ? (
              <textarea
                disabled={!canManage}
                value={form?.[f.key] || ""}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                className="input-tactical w-full min-h-[90px] resize-y"
                data-testid={`pi-input-${f.key}`}
              />
            ) : (
              <input
                disabled={!canManage}
                value={form?.[f.key] || ""}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                className={`input-tactical w-full ${f.mono ? "font-mono" : ""}`}
                data-testid={`pi-input-${f.key}`}
              />
            )}
            {f.hint && <div className="text-[10px] text-[#71717A] mt-1">{f.hint}</div>}
          </div>
        ))}
      </div>

      {canManage && (
        <div className="flex items-center justify-between pt-4 border-t border-[#27272A]">
          <div className="text-[10px] text-[#71717A]">{dirty ? "Tienes cambios sin guardar." : "Todo guardado."}</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setForm(info)}
              disabled={!dirty || saving}
              className="btn-ghost text-xs"
              data-testid="pi-cancel"
            >
              DESCARTAR
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="btn-primary text-xs flex items-center gap-1.5"
              data-testid="pi-save"
            >
              <Save size={12}/> {saving ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =================== PAYMENT INFO MODAL (Player view) ===================
function PaymentInfoModal({ onClose }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/club/payment-info")
      .then(r => setInfo(r.data))
      .catch(() => toast.error("No se pudieron cargar los datos bancarios"))
      .finally(() => setLoading(false));
  }, []);

  const copy = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const Row = ({ label, value, mono, testid }) => (
    <div className="flex items-center justify-between gap-3 border-b border-[#27272A] py-3 last:border-b-0" data-testid={testid}>
      <div className="min-w-0">
        <div className="overline text-[#71717A]">{label}</div>
        <div className={`text-white mt-0.5 break-words ${mono ? "font-mono text-base" : "text-sm"}`}>{value || "—"}</div>
      </div>
      {value && (
        <button
          type="button"
          onClick={() => copy(label, value)}
          className="btn-ghost text-[10px] flex items-center gap-1 shrink-0 border border-[#27272A] hover:border-[#34C759] hover:text-[#34C759]"
          data-testid={`${testid}-copy`}
        >
          <Copy size={10}/> COPIAR
        </button>
      )}
    </div>
  );

  return (
    <ModalShell title="DATOS PARA TRANSFERENCIA" subtitle="Cuenta del club" onClose={onClose}>
      {loading && <div className="text-center text-[#71717A] text-sm py-6">Cargando...</div>}
      {!loading && info && (
        <div data-testid="payment-info-content">
          <div className="bg-[#1A1A1A] border-l-2 border-[#34C759] p-3 mb-4">
            <div className="overline text-[#34C759] mb-1">Cómo pagar</div>
            <div className="text-xs text-[#A1A1AA] leading-relaxed">
              1. Realiza la transferencia con los datos de abajo · 2. Guarda el comprobante · 3. Vuelve y aprieta <span className="text-white font-bold">"Informar pago"</span> en tu cuota pendiente.
            </div>
          </div>
          <div className="border border-[#27272A]">
            <Row label="Banco" value={info.bank} testid="pi-bank"/>
            <Row label="Tipo de cuenta" value={info.account_type} testid="pi-type"/>
            <Row label="Nº de cuenta" value={info.account_number} mono testid="pi-acct"/>
            <Row label="RUT" value={info.rut} mono testid="pi-rut"/>
            <Row label="Titular" value={info.holder} testid="pi-holder"/>
            <Row label="Email del club" value={info.email} testid="pi-email"/>
          </div>
          {info.instructions && (
            <div className="bg-[#1A1A1A] border-l-2 border-[#FF9F0A] p-3 mt-4">
              <div className="overline text-[#FF9F0A] mb-1">Instrucciones</div>
              <div className="text-xs text-[#A1A1AA] leading-relaxed">{info.instructions}</div>
            </div>
          )}
          <div className="flex justify-end pt-4 border-t border-[#27272A] mt-4">
            <button type="button" onClick={onClose} className="btn-primary text-xs" data-testid="pi-close">ENTENDIDO</button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// =================== SHARED ===================
const Field = ({ label, children }) => (
  <div>
    <label className="label-tactical">{label}</label>
    {children}
  </div>
);

const ModalActions = ({ onClose, saving, submitLabel, testid }) => (
  <div className="flex gap-2 justify-end pt-3 border-t border-[#27272A]">
    <button type="button" onClick={onClose} className="btn-ghost text-xs">CANCELAR</button>
    <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid={testid}>{saving ? "GUARDANDO..." : submitLabel}</button>
  </div>
);

function ModalShell({ title, subtitle, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`tactical-card p-6 w-full ${wide ? "max-w-4xl" : "max-w-xl"} my-8`}>
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <div className="overline">{subtitle || "Acción"}</div>
            <div className="font-display text-2xl text-white truncate">{title}</div>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost text-xs">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}


// =================== DELINQUENCY PANEL (Morosos + Recordatorios) ===================
function DelinquencyPanel({ data, canManage, onChanged }) {
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const items = data?.items || [];
  const summary = data?.summary || { delinquent_count: 0, total_owed: 0 };
  const allSelected = items.length > 0 && items.every(i => selected.has(i.user_id));

  const toggle = (uid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(items.map(i => i.user_id)));
  };

  const sendReminders = async (userIds) => {
    if (!canManage) return;
    const ids = userIds && userIds.length ? userIds : null;
    const label = ids ? `${ids.length} jugador${ids.length === 1 ? "" : "es"}` : `${items.length} morosos`;
    if (!confirm(`¿Enviar recordatorio de pago a ${label}?\n\nNOTA: Es una simulación. Quedará registrada en el historial pero no se envía email real.`)) return;
    setSending(true);
    try {
      const r = await api.post("/finance/reminders/send", { user_ids: ids });
      toast.success(`${r.data.sent} recordatorio${r.data.sent === 1 ? "" : "s"} registrado${r.data.sent === 1 ? "" : "s"} (simulación)`);
      setSelected(new Set());
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar");
    } finally { setSending(false); }
  };

  const loadLogs = async () => {
    try {
      const r = await api.get("/finance/reminders/logs");
      setLogs(r.data);
      setShowLogs(true);
    } catch {
      toast.error("Error al cargar historial");
    }
  };

  return (
    <div className="space-y-4" data-testid="delinquency-panel">
      {/* Header / Summary */}
      <div className="tactical-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#FF3B30]/10 border border-[#FF3B30] flex items-center justify-center">
              <AlertOctagon className="text-[#FF3B30]" size={18}/>
            </div>
            <div>
              <div className="overline text-[#FF3B30]">Mora</div>
              <div className="font-display text-2xl text-white leading-none">{summary.delinquent_count} {summary.delinquent_count === 1 ? "JUGADOR MOROSO" : "JUGADORES MOROSOS"}</div>
              <div className="text-xs text-[#A1A1AA] mt-1">
                Adeudan 2 o más meses de cuota. Total deuda: <span className="text-[#FF3B30] font-bold">{fmtMoney(summary.total_owed)}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadLogs}
              className="btn-ghost text-xs flex items-center gap-1.5 border border-[#27272A]"
              data-testid="view-reminder-logs"
            >
              <History size={12}/> Historial
            </button>
            {canManage && (
              <>
                <button
                  onClick={() => sendReminders(Array.from(selected))}
                  disabled={sending || selected.size === 0}
                  className="btn-ghost text-xs flex items-center gap-1.5 border border-[#FACC15] text-[#FACC15] disabled:opacity-40"
                  data-testid="send-selected-reminders"
                >
                  <Send size={12}/> Enviar a seleccionados ({selected.size})
                </button>
                <button
                  onClick={() => sendReminders(null)}
                  disabled={sending || items.length === 0}
                  className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="send-all-reminders"
                >
                  <BellRing size={12}/> Enviar a todos
                </button>
              </>
            )}
          </div>
        </div>
        <div className="text-[10px] text-[#71717A] mt-3 pl-13 flex items-start gap-1.5">
          <Mail size={10} className="mt-px"/>
          <span>
            Los recordatorios se simulan internamente (no se envía email real). Quedan registrados en el historial.
            Se sugiere ejecutar el envío masivo el <strong className="text-[#A1A1AA]">día 15 de cada mes</strong> para alertar a quienes no pagaron la cuota anterior.
          </span>
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="delinquency-empty">
          <CheckCircle2 className="mx-auto text-[#34C759] mb-3" size={36}/>
          <div className="font-display text-xl text-white">SIN MOROSOS</div>
          <div className="text-xs text-[#A1A1AA] mt-2">Todos los jugadores están al día (o adeudan menos de 2 meses).</div>
        </div>
      ) : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
              <tr>
                {canManage && (
                  <th className="p-3 text-left w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      data-testid="select-all-morosos"
                      className="accent-[#FF3B30]"
                    />
                  </th>
                )}
                <th className="p-3 text-left overline">Jugador</th>
                <th className="p-3 text-left overline">Email</th>
                <th className="p-3 text-left overline">Meses</th>
                <th className="p-3 text-left overline">Detalle</th>
                <th className="p-3 text-right overline">Deuda</th>
                <th className="p-3 text-left overline">Último recordatorio</th>
                {canManage && <th className="p-3 text-right overline">Acción</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.user_id} className="border-b border-[#1A1A1A] hover:bg-[#0A0A0A]" data-testid={`moroso-row-${it.user_id}`}>
                  {canManage && (
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(it.user_id)}
                        onChange={() => toggle(it.user_id)}
                        data-testid={`select-moroso-${it.user_id}`}
                        className="accent-[#FF3B30]"
                      />
                    </td>
                  )}
                  <td className="p-3 text-white font-bold">{it.name}</td>
                  <td className="p-3 text-[#A1A1AA] text-xs">{it.email}</td>
                  <td className="p-3">
                    <span className="text-[10px] px-1.5 py-0.5 border border-[#FF3B30] text-[#FF3B30] uppercase tracking-wider font-bold">
                      {it.months_overdue} {it.months_overdue === 1 ? "mes" : "meses"}
                    </span>
                  </td>
                  <td className="p-3 text-[10px] text-[#71717A] font-mono">{(it.overdue_months || []).join(", ")}</td>
                  <td className="p-3 text-right text-[#FF3B30] font-bold font-mono">{fmtMoney(it.pending_total)}</td>
                  <td className="p-3 text-xs text-[#A1A1AA]">{fmtDate(it.last_reminder_sent)}</td>
                  {canManage && (
                    <td className="p-3 text-right">
                      <button
                        onClick={() => sendReminders([it.user_id])}
                        disabled={sending}
                        className="btn-ghost text-[10px] flex items-center gap-1 border border-[#FACC15] text-[#FACC15] ml-auto disabled:opacity-40"
                        data-testid={`remind-${it.user_id}`}
                      >
                        <Send size={10}/> Recordar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showLogs && <ReminderLogsModal logs={logs} onClose={() => setShowLogs(false)}/>}
    </div>
  );
}

function ReminderLogsModal({ logs, onClose }) {
  return (
    <ModalShell title="HISTORIAL DE RECORDATORIOS" subtitle="Trazabilidad" onClose={onClose} wide>
      <div className="max-h-[60vh] overflow-y-auto" data-testid="reminder-logs">
        {logs.length === 0 ? (
          <div className="p-10 text-center text-[#71717A] text-sm">Aún no se han enviado recordatorios.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-[#0A0A0A] border-b border-[#27272A] sticky top-0">
              <tr>
                <th className="p-2 text-left overline">Fecha</th>
                <th className="p-2 text-left overline">Jugador</th>
                <th className="p-2 text-left overline">Email</th>
                <th className="p-2 text-left overline">Meses</th>
                <th className="p-2 text-right overline">Monto</th>
                <th className="p-2 text-left overline">Canal</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className="border-b border-[#1A1A1A]">
                  <td className="p-2 text-[#A1A1AA] font-mono">{fmtDate(l.sent_at)}</td>
                  <td className="p-2 text-white font-bold">{l.user_name}</td>
                  <td className="p-2 text-[#71717A]">{l.user_email}</td>
                  <td className="p-2 text-[#FF3B30] font-bold">{l.months_overdue}</td>
                  <td className="p-2 text-right text-[#FF3B30] font-mono">{fmtMoney(l.pending_total)}</td>
                  <td className="p-2">
                    <span className="text-[10px] px-1.5 py-px border border-[#A78BFA] text-[#A78BFA] uppercase tracking-wider">
                      {l.channel === "simulation" ? "Simulación" : l.channel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </ModalShell>
  );
}
